'use client';

import {
  ColumnFiltersState,
  PaginationState,
  RowData,
  SortingState,
  Table,
  TableMeta,
} from '@tanstack/react-table';
import { useCallback, useMemo, useRef } from 'react';
import { z } from 'zod';

import { useSearch } from 'src/hooks';

import { searchToTableQuery, tableQueryToSearch, type TableQuery } from './helper';
import { Query } from './types';

export interface UseDataTableSearchReturn {
  // 状态
  sorting?: SortingState;
  pagination?: PaginationState;
  columnFilters?: ColumnFiltersState;

  // 更新函数
  onSortingChange:
    | ((updater: SortingState | ((old: SortingState) => SortingState)) => void)
    | undefined;
  onPaginationChange:
    | ((updater: PaginationState | ((old: PaginationState) => PaginationState)) => void)
    | undefined;
  onColumnFiltersChange: (
    updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)
  ) => void;

  // 统计
  getFacetedUniqueValues?: <TData extends RowData>() => (
    table: Table<TData>,
    columnId: string
  ) => () => Map<any, number>;

  // Manual 模式参数
  manualPagination: boolean;
  manualFiltering: true;
  manualSorting: boolean;

  // 是否处于过渡状态
  pending: boolean;

  // meta
  meta?: TableMeta<any>;
}

export interface UseDataTableSearchOptions<T extends z.ZodRawShape> {
  /** 过滤器字段的 Zod schema，不包含基础字段 */
  filterSchema: z.ZodObject<T>;
  facets?: { [columnId: string]: Map<any, number> };
  manualPagination?: boolean;
  manualSorting?: boolean;
}

/**
 * useDataTableSearch Hook
 *
 * 用于处理 DataTable 的排序、分页、过滤参数，并自动同步到 URL
 *
 * @param options 配置选项，包含过滤器字段的 schema
 * @returns 返回 sorting、pagination、columnFilters 的状态和更新函数
 */
export function useDataTableSearch<T extends z.ZodRawShape>(
  options: UseDataTableSearchOptions<T>
): UseDataTableSearchReturn {
  const { filterSchema, facets, manualPagination = true, manualSorting = true } = options;
  const { search, searchHandler, pending } = useSearch<Query<T>>({
    scroll: false,
  });

  // 使用 ref 存储最新的 searchHandler
  const searchHandlerRef = useRef(searchHandler);
  searchHandlerRef.current = searchHandler;

  // 使用 utils 中的方法将 search 转换为 TableQuery 格式
  const tableQuery: TableQuery = useMemo(() => {
    return searchToTableQuery(search, filterSchema);
  }, [search, filterSchema]);

  // 排序变化处理
  const onSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting =
        typeof updater === 'function' ? updater(tableQuery.sorting || []) : updater;

      // 使用 utils 方法转换为 search 对象并更新 URL
      const newSearch = tableQueryToSearch({ sorting: newSorting });
      searchHandlerRef.current.set((old: Query<T>) => ({
        ...old,
        sort: newSearch.sort,
      }));
    },
    [tableQuery]
  );

  // 分页变化处理
  const onPaginationChange = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
      const newPagination =
        typeof updater === 'function'
          ? updater(tableQuery.pagination ?? { pageIndex: 0, pageSize: 10 })
          : updater;

      // 使用 utils 方法转换为 search 对象并更新 URL
      const newSearch = tableQueryToSearch({ pagination: newPagination });
      searchHandlerRef.current.set((old: Query<T>) => ({
        ...old,
        page: newSearch.page,
        size: newSearch.size,
      }));
    },
    [tableQuery]
  );

  // 过滤变化处理
  const onColumnFiltersChange = useCallback(
    (updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
      const newFilters =
        typeof updater === 'function' ? updater(tableQuery.columnFilters || []) : updater;

      // 构建新的 TableQuery（过滤变化时重置到第一页）
      const newTableQuery: TableQuery = {
        columnFilters: newFilters,
        pagination: tableQuery.pagination ? { ...tableQuery.pagination, pageIndex: 0 } : undefined,
      };

      // 使用 utils 方法转换为 search 对象并更新 URL
      const newSearch = tableQueryToSearch(newTableQuery);
      const filterKeys = Object.keys(filterSchema.shape) as string[];

      // 使用 updater 函数来清除不在 filterKeys 中的过滤字段
      searchHandlerRef.current.set((old: Query<T>) => {
        // 从 filterKeys 数组构造一个清空对象（将所有 filterKeys 设置为 undefined）
        const clearFilterObj = Object.fromEntries(
          filterKeys.map((key) => [key, undefined])
        ) as Partial<Query<T>>;

        // 先清空所有过滤字段，然后应用新的搜索参数，保留分页
        const result = {
          ...old,
          ...clearFilterObj,
          ...newSearch,
        };
        return result;
      });
    },
    [tableQuery, filterSchema]
  );

  const facetsRef = useRef(facets);
  facetsRef.current = facets;

  const getFacetedUniqueValues = useCallback(() => {
    return <TData extends RowData>(_table: Table<TData>, columnId: string) => {
      return () => facetsRef.current?.[columnId] || new Map();
    };
  }, []);

  return {
    ...tableQuery,
    onSortingChange: manualSorting ? onSortingChange : undefined,
    onPaginationChange: manualPagination ? onPaginationChange : undefined,
    onColumnFiltersChange,
    manualPagination: manualPagination,
    manualFiltering: true,
    manualSorting: manualSorting,
    pending,
    getFacetedUniqueValues: manualPagination ? getFacetedUniqueValues : undefined,
  };
}
